import { bus } from '@core/events/eventSystem';
import { getStore } from '@core/store';
import constants from '@core/utils/constants';
import orderSerializer from './serializer.js';

// Get user store reference
function getUserId() {
  const userStore = getStore('user');
  if (userStore?.state?.current?.id) {
    return userStore.state.current.id;
  }
}
function getUserData() {
  const userStore = getStore('user');
  if (userStore?.state?.current) {
    return userStore.state.current;
  }
  return null;
}

const handleListResponse = (res) => {
  if (res.id === 'load-orders') {
    // Handle 404 "Order not found" as empty list (not an error)
    const errorData = res.data?.error?.error || res.data?.error || res.error;
    if (
      errorData?.statusCode === 404 ||
      errorData?.code === 'APP_ERROR' ||
      (errorData?.message && errorData.message.includes('Order not found'))
    ) {
      bus.emit(constants.EVENTS.ORDERS.LOADED, []);
      return;
    }

    if (res.success || res.data?.success) {
      // Use serializer to deserialize orders list
      const orders = orderSerializer.deserializeList(res);
      bus.emit(constants.EVENTS.ORDERS.LOADED, orders);
    } else {
      const error = orderSerializer.deserializeError(res.data || res);
      bus.emit(
        constants.EVENTS.ORDERS.LOAD_ERROR,
        error.message || 'Erreur lors du chargement'
      );
    }
  }
};

// Load orders
bus.on(constants.EVENTS.ORDERS.LIST, async (status = '') => {
  try {
    bus.emit(constants.EVENTS.ORDERS.LOADING);

    // Fixed: page=1 (server expects >= 1), limit=100, no pagination
    const userId = getUserId();
    if (!userId) {
      console.warn('[OrderController] Cannot list orders: userId is missing');
      bus.emit(constants.EVENTS.ORDERS.LOAD_ERROR, 'Utilisateur non identifié');
      return;
    }

    const params = new URLSearchParams({ userId, limit: 1000, offset: 0 });
    if (status && typeof status == 'string') params.set('status', status);
    const url = `${constants.ENDPOINTS.ORDERS.LIST}&${params}`;

    bus.emit(constants.EVENTS.NETWORK.REQUEST, {
      id: 'load-orders',
      url,
      method: 'GET'
    });

    // Cleanup previous listener to avoid duplicates if any
    bus.off(constants.EVENTS.NETWORK.RESPONSE, handleListResponse);
    bus.on(constants.EVENTS.NETWORK.RESPONSE, handleListResponse);
  } catch (error) {
    bus.emit(constants.EVENTS.ORDERS.LOAD_ERROR, error.message);
  }
});
// Load single order detail
bus.on(constants.EVENTS.ORDERS.DETAIL, async (orderPayload) => {
  const orderId =
    typeof orderPayload === 'object'
      ? orderPayload?.id || orderPayload?.orderId
      : orderPayload;
  if (!orderId) return;
  const requestId = `order-detail-${orderId}`;

  try {
    bus.emit(constants.EVENTS.ORDERS.DETAIL_LOADING);

    const url = `${constants.ENDPOINTS.ORDERS.DETAIL}`.replace(
      '{orderId}',
      orderId
    );

    const responseHandler = (res) => {
      if (res.id !== requestId) return;
      bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

      if (res.success) {
        const raw = res.data?.data || res.data || res;
        const order = orderSerializer.deserializeOrder(raw);
        bus.emit(constants.EVENTS.ORDERS.DETAIL_LOADED, order);
      } else {
        const error = orderSerializer.deserializeError(res.data || res);
        bus.emit(
          constants.EVENTS.ORDERS.DETAIL_ERROR,
          error.message || 'Erreur'
        );
      }
    };

    bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

    bus.emit(constants.EVENTS.NETWORK.REQUEST, {
      id: requestId,
      url,
      method: 'GET'
    });
  } catch (error) {
    bus.emit(constants.EVENTS.ORDERS.DETAIL_ERROR, error.message);
  }
});

bus.on(constants.EVENTS.ORDERS.CREATE, async (orderData) => {
  const requestId = `orders-create-${Date.now()}`;

  console.log('[OrderController] Creating order:', orderData);

  try {
    bus.emit(constants.EVENTS.ORDERS.CREATING);
    bus.emit(constants.EVENTS.LOADING.START);

    // Serialize data for API
    const restaurant = await getRestaurant(orderData.cartData.restaurantId);

    if (!restaurant?.isOpen) {
      bus.emit(constants.EVENTS.CART.ITEMS.ADD_ERROR, {
        message: 'Restaurant is closed'
      });
      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        type: 'error',
        title: 'Restaurant fermé',
        body: 'Ce restaurant est fermé. Veuillez réessayer plus tard.'
      });
      setTimeout(() => {
        bus.emit(constants.EVENTS.LOADING.FINISH);
      }, 300);
      return;
    }

    const payload = await orderSerializer.serialize({
      ...orderData,
      restaurant,
      userId: getUserId(),
      userData: getUserData()
    });

    console.log('[OrderController] Order creation payload:', payload);

    // Emit network request
    bus.emit(constants.EVENTS.NETWORK.REQUEST, {
      id: requestId,
      url: constants.ENDPOINTS.ORDERS.CREATE,
      method: 'POST',
      data: payload
    });

    // Handle response
    const responseHandler = async (res) => {
      if (res.id === requestId) {
        bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

        setTimeout(() => {
          bus.emit(constants.EVENTS.LOADING.FINISH);
        }, 300);

        if (res.success) {
          // Rename to avoid shadowing the outer `orderData` (event arg with paymentData)
          const rawOrder = res.data?.data || res.data || res;
          const createdOrder = orderSerializer.deserializeOrder(rawOrder);
          console.log('[OrderController] Order created:', createdOrder);

          // Resolve orderId from multiple possible sources in the API response
          const orderId = createdOrder.id || rawOrder?.id || rawOrder?._id;
          // Resolve total from pricing, then cart store as fallback
          const cartStore = window.$stores?.cart;
          const orderTotal =
            createdOrder.totalPrice ||
            rawOrder?.pricing?.total ||
            rawOrder?.total ||
            rawOrder?.totalPrice ||
            cartStore?.state?.fees?.total ||
            0;

          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'success',
            title: 'Commande créée',
            body: 'Votre commande a été enregistrée avec succès!'
          });

          // paymentData comes from the original ORDERS.CREATE event (CartPaymentDialog output)
          // CartPaymentDialog emits method as 'mobileMoney', normalize to compare
          const rawPaymentMethod =
            orderData.paymentData?.method ||
            payload.payment?.method ||
            createdOrder.paymentMethod ||
            createdOrder.payment?.method ||
            'cash';
          const normalizedMethod = rawPaymentMethod
            ?.toLowerCase()
            .replace(/[-_]/g, ''); // 'mobilemoney', 'wallet', 'cash'

          const userStore = getStore('user');
          const user = userStore?.state?.current || {};
          // phoneNumber is provided by CartPaymentDialog for mobile_money
          const phoneNumber =
            orderData.paymentData?.phoneNumber ||
            payload.payment?.phoneNumber ||
            user.phone ||
            user.phoneNumber ||
            '';
          const firstName =
            user.firstName || user.name?.split(' ')[0] || 'Client';
          const lastName =
            user.lastName ||
            user.name?.split(' ').slice(1).join(' ') ||
            'Rapidoo';

          console.log(
            '[OrderController] Payment — orderId:',
            orderId,
            'amount:',
            orderTotal,
            'method:',
            rawPaymentMethod,
            '→',
            normalizedMethod
          );

          if (normalizedMethod === 'mobilemoney') {
            const txId = `${orderId}-mm-${Date.now()}`;
            const txRequestId = `REQ-${orderId}-${Date.now()}`;
            console.log(
              '[OrderController] Initiating mobile money payment, phone:',
              phoneNumber
            );
            const provider = await getProviderToUse(true);
            console.log('[OrderController] Provider:', provider);
              if (!provider) {
                bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
                  type: 'error',
                  title: 'Erreur de paiement',
                  body: 'Impossible de déterminer le fournisseur de paiement'
                });
                return;
              }

            bus.emit(constants.EVENTS.NETWORK.REQUEST, {
              id: `payment-mm-${orderId}`,
              url: 'https://api.monrapidoo.com/payment/collect/mobileMoney?group=ultraxeno',
              method: 'POST',
              data: {
                id: txId,
                requestId: txRequestId,
                firstName: firstName,
                lastName: lastName,
                service: 'rapidoo',
                amount: orderTotal,
                provider,
                countryCode: 'BJ',
                currency: 'XOF',
                phoneNumber: phoneNumber,
                narration: 'order payment',
                metadata: {
                  provider,
                  id: txId,
                  requestId: txRequestId,
                  accountNumber: phoneNumber,
                  eventTopic: 'rapidoo-transactions',
                  eventToEmit: 'orderPaymentCollected',
                  order: createdOrder
                }
              }
            });
          } else if (normalizedMethod === 'wallet') {
            const txId = `${orderId}-wallet-${Date.now()}`;
            const txRequestId = `REQ-${orderId}-${Date.now()}`;
            console.log(
              '[OrderController] Initiating wallet debit for order:',
              orderId
            );
            bus.emit(constants.EVENTS.NETWORK.REQUEST, {
              id: `payment-wallet-${orderId}`,
              url: 'https://api.monrapidoo.com/wallet/debit?group=ultraxeno',
              method: 'POST',
              data: {
                id: txId,
                userId: `wallet_${getUserId()}`,
                service: 'rapidoo',
                country: 'ANY',
                processor: 'rapidoWallet',
                currency: 'XOF',
                amount: orderTotal,
                reqId: txRequestId,
                metadata: {
                  accountNumber: phoneNumber,
                  eventTopic: 'rapidoo-transactions',
                  eventToEmit: 'orderWalletWithdrawn',
                  order: createdOrder
                }
              }
            });
          }

          bus.emit(constants.EVENTS.ORDERS.CREATED, createdOrder);
          // Empty the cart after successful order creation
          bus.emit(constants.EVENTS.CART.CLEAR);
          bus.emit(constants.EVENTS.ORDERS.LOAD);
        } else {
          const error = orderSerializer.deserializeError(res);
          console.error('[OrderController] Order creation error:', error);

          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'error',
            title: 'Erreur',
            body: error.message || 'Impossible de créer la commande'
          });

          bus.emit(constants.EVENTS.ORDERS.CREATE_ERROR, error);
        }
      }
    };

    bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);
  } catch (error) {
    console.error('[OrderController] Error creating order:', error);
    setTimeout(() => {
      bus.emit(constants.EVENTS.LOADING.FINISH);
    }, 300);

    bus.emit(constants.EVENTS.ORDERS.CREATE_ERROR, {
      message: error.message,
      code: 'ORDER_CREATE_ERROR'
    });
  }
});

// Cancel order
bus.on(constants.EVENTS.ORDERS.CANCEL, async (orderId) => {
  const requestId = `orders-cancel-${Date.now()}`;

  console.log('[OrderController] Cancelling order:', orderId);

  try {
    bus.emit(constants.EVENTS.ORDERS.CANCELLING);
    bus.emit(constants.EVENTS.LOADING.START);

    // Emit network request
    bus.emit(constants.EVENTS.NETWORK.REQUEST, {
      id: requestId,
      url: `${constants.ENDPOINTS.ORDERS.CANCEL}`,
      method: 'POST',
      data: { userId: getUserId(), orderId }
    });

    // Handle response
    const responseHandler = (res) => {
      if (res.id === requestId) {
        bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

        setTimeout(() => {
          bus.emit(constants.EVENTS.LOADING.FINISH);
        }, 300);

        if (res.success) {
          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'success',
            title: 'Commande annulée',
            body: 'Votre commande a été annulée avec succès!'
          });

          bus.emit(constants.EVENTS.ORDERS.CANCELLED, orderId);
          bus.emit(constants.EVENTS.ORDERS.LOAD);
        } else {
          const error = orderSerializer.deserializeError(res);
          console.error('[OrderController] Order cancel error:', error);

          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'error',
            title: 'Erreur',
            body: error.message || "Impossible d'annuler la commande"
          });

          bus.emit(constants.EVENTS.ORDERS.CANCEL_ERROR, error);
        }
      }
    };

    bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);
  } catch (error) {
    console.error('[OrderController] Error cancelling order:', error);
    setTimeout(() => {
      bus.emit(constants.EVENTS.LOADING.FINISH);
    }, 300);

    bus.emit(constants.EVENTS.ORDERS.CANCEL_ERROR, {
      message: error.message,
      code: 'ORDER_CANCEL_ERROR'
    });
  }
});

// Rate order
bus.on(constants.EVENTS.ORDERS.RATE, async (data) => {
  const { orderId, rating, comment } = data;
  const requestId = `orders-rate-${Date.now()}`;

  console.log('[OrderController] Rating order:', orderId, rating);

  try {
    bus.emit(constants.EVENTS.ORDERS.RATING);
    bus.emit(constants.EVENTS.LOADING.START);

    // Emit network request
    bus.emit(constants.EVENTS.NETWORK.REQUEST, {
      id: requestId,
      url: `${constants.ENDPOINTS.ORDERS.RATE}`,
      method: 'POST',
      data: { orderId, rating, comment }
    });

    // Handle response
    const responseHandler = (res) => {
      if (res.id === requestId) {
        bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

        setTimeout(() => {
          bus.emit(constants.EVENTS.LOADING.FINISH);
        }, 300);

        if (res.success) {
          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'success',
            title: 'Merci!',
            body: 'Votre avis a été enregistré avec succès!'
          });

          bus.emit(constants.EVENTS.ORDERS.RATED, { orderId, rating });
        } else {
          const error = orderSerializer.deserializeError(res);
          console.error('[OrderController] Order rate error:', error);

          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'error',
            title: 'Erreur',
            body: error.message || "Impossible d'enregistrer votre avis"
          });

          bus.emit(constants.EVENTS.ORDERS.RATE_ERROR, error);
        }
      }
    };

    bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);
  } catch (error) {
    console.error('[OrderController] Error rating order:', error);
    setTimeout(() => {
      bus.emit(constants.EVENTS.LOADING.FINISH);
    }, 300);

    bus.emit(constants.EVENTS.ORDERS.RATE_ERROR, {
      message: error.message,
      code: 'ORDER_RATE_ERROR'
    });
  }
});

async function getRestaurant(restaurantId) {
  const restaurantRequestId = `fetchRestaurant-${Date.now()}`;
  const restaurantUrl = `${constants.ENDPOINTS.RESTAURANTS.DETAIL}`.replace(
    '{id}',
    restaurantId
  );

  console.log('[Cart Controller] Fetching restaurant:', restaurantUrl);

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: restaurantRequestId,
    url: restaurantUrl,
    method: 'GET',
    data: {}
  });

  // Wait for restaurant response
  const restaurantPromise = new Promise((resolve, reject) => {
    const handler = (res) => {
      if (res.id === restaurantRequestId) {
        bus.off(constants.EVENTS.NETWORK.RESPONSE, handler);
        if (res.success && res.data?.data) {
          resolve(res.data.data);
        } else {
          reject(new Error('Failed to fetch restaurant'));
        }
      }
    };
    bus.on(constants.EVENTS.NETWORK.RESPONSE, handler);
  });

  const restaurant = await restaurantPromise;
  return restaurant;
}

export default {
  getUserId
};

async function getProviderToUse(isCollect) {
  try {
    const feeUrl =
      'https://api.monrapidoo.com/settings/byKey?group=ultraxeno&key=fees&service=rapidoo';
    const res = await fetch(feeUrl);

    if (!res.ok) {
      return null;
    }

    const responseData = await res.json();

    // Vérification du status code API et de la présence des paramètres requis
    if (
      responseData?.statusCode !== 200 ||
      !responseData?.data?.paymentProviders
    ) {
      return null;
    }

    const collectionProviders =
      responseData.data.paymentProviders.collectionProviders;
    const withdrawalProviders =
      responseData.data.paymentProviders.withdrawalProviders;

    if (isCollect) {
      return collectionProviders;
    } else {
      return withdrawalProviders;
    }
  } catch (error) {
    console.error(
      'Erreur lors de la récupération du fournisseur de paiement :',
      error
    );
    return null;
  }
}
